import { useEffect, useMemo, useRef, useState } from "react";

import { useSaveTextbausteine, useTextbausteine } from "../../hooks/useStammdaten";
import { errorText, type SnippetListe } from "../../lib/invoices";
import type { TextBaustein, Textbausteine } from "../../lib/types";

import "../../styles/bibliothek.css";

/**
 * Die Textbaustein-Bibliothek — alle vier Listen an einem Ort.
 *
 * Bis hierher konnte der Belegeditor einen Baustein nur **einfügen** und unter
 * einem Namen **zurückschreiben**. Umbenennen ohne Dublette, Löschen und vor
 * allem die Frage „welcher ist der Standard?" gingen gar nicht — dabei steht
 * die Antwort längst in der Datei (`defaults.<liste>_label`), und genau daraus
 * belegt `createInvoiceDraft` Kopf- und Fußtext eines neuen Belegs vor.
 *
 * ⚠️ **Gespeichert wird die ganze Datei.** `textbausteine_schreiben` ist eine
 * Vollersetzung aller vier Listen — derselbe Weg, mit dem sich beim
 * Positionskatalog schon einmal 72 Einträge gelöscht haben, weil der Aufrufer
 * nur einen Ausschnitt kannte. Dieses Overlay darf ihn benutzen, weil
 * `useTextbausteine` den vollen Bestand lädt; solange der nicht da ist, bleibt
 * der Speichern-Knopf gesperrt. Der schmale Weg aus der Editor-Karte
 * (`POST /rechnungen/editor/textbaustein`) bleibt davon unberührt.
 */

const LISTEN: { id: SnippetListe; titel: string; sinn: string }[] = [
  { id: "intro_invoice", titel: "Kopftext Rechnung", sinn: "Steht über der Positionstabelle." },
  { id: "intro_quote", titel: "Kopftext Angebot", sinn: "Steht über der Positionstabelle." },
  { id: "notes_invoice", titel: "Fußtext Rechnung", sinn: "Steht unter dem Gesamtbetrag." },
  { id: "notes_quote", titel: "Fußtext Angebot", sinn: "Steht unter dem Gesamtbetrag." },
];

/** Was `applySnippetPlaceholders` beim Einfügen ersetzt — mehr kennt es nicht. */
const PLATZHALTER = [
  { marke: "{{FAELLIGKEIT_DE}}", hilfe: "Fälligkeitsdatum, z. B. 30.09.2026" },
  { marke: "{{RECHNUNGSDATUM_DE}}", hilfe: "Belegdatum" },
  { marke: "{{ZAHLUNGSZIEL_TAGE}}", hilfe: "Zahlungsziel als Zahl, z. B. 14" },
  { marke: "{{ZAHLUNGSZIEL_TAGE_WORT}}", hilfe: "Zahlungsziel ausgeschrieben, z. B. 14 Tage" },
];

const standardSchluessel = (liste: SnippetListe) => `${liste}_label` as keyof Textbausteine["defaults"];

type Props = {
  /** Mit welcher Liste sich das Overlay öffnet — die der aufrufenden Karte. */
  startListe?: SnippetListe;
  onClose: () => void;
};

export default function TextbausteinBibliothek({ startListe = "intro_invoice", onClose }: Props) {
  const bestand = useTextbausteine();
  const sichern = useSaveTextbausteine();
  const [liste, setListe] = useState<SnippetListe>(startListe);
  /** Arbeitskopie — erst „Speichern" schreibt sie zurück. */
  const [daten, setDaten] = useState<Textbausteine | null>(null);
  const [offen, setOffen] = useState<number | null>(null);
  const [meldung, setMeldung] = useState("");
  const textfeld = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (bestand.data && !daten) setDaten(bestand.data); }, [bestand.data, daten]);
  useEffect(() => { setOffen(null); }, [liste]);

  useEffect(() => {
    const taste = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [onClose]);

  const eintraege = daten?.[liste] || [];
  const standard = daten ? String(daten.defaults[standardSchluessel(liste)] || "") : "";
  const geaendert = useMemo(
    () => Boolean(daten && bestand.data && JSON.stringify(daten) !== JSON.stringify(bestand.data)),
    [daten, bestand.data],
  );

  const setzeListe = (neu: TextBaustein[]) => {
    if (!daten) return;
    setDaten({ ...daten, [liste]: neu });
  };

  const aendern = (index: number, teil: Partial<TextBaustein>) => {
    if (!daten) return;
    const neu = eintraege.map((eintrag, i) => (i === index ? { ...eintrag, ...teil } : eintrag));
    // Der Standard zeigt auf den Namen. Wird der umbenannt, muss der Zeiger
    // mit — sonst steht in der Datei ein Standard, den es nicht mehr gibt, und
    // ein neuer Beleg startet still ohne Text.
    const alterName = eintraege[index]?.label || "";
    const neuerName = teil.label;
    const defaults = neuerName !== undefined && alterName === standard
      ? { ...daten.defaults, [standardSchluessel(liste)]: neuerName }
      : daten.defaults;
    setDaten({ ...daten, [liste]: neu, defaults });
  };

  const loeschen = (index: number) => {
    if (!daten) return;
    const weg = eintraege[index];
    const neu = eintraege.filter((_, i) => i !== index);
    const defaults = weg?.label === standard
      ? { ...daten.defaults, [standardSchluessel(liste)]: neu[0]?.label || "" }
      : daten.defaults;
    setDaten({ ...daten, [liste]: neu, defaults });
    setOffen(null);
    setMeldung(`„${weg?.label || "Ohne Namen"}" entfernt — gespeichert wird erst mit „Speichern".`);
  };

  const zumStandard = (index: number) => {
    if (!daten) return;
    setDaten({ ...daten, defaults: { ...daten.defaults, [standardSchluessel(liste)]: eintraege[index]?.label || "" } });
  };

  const anlegen = () => {
    setzeListe([...eintraege, { label: "Neuer Baustein", text: "" }]);
    setOffen(eintraege.length);
    setMeldung("");
  };

  const platzhalterEinfuegen = (marke: string) => {
    if (offen === null) return;
    const feld = textfeld.current;
    const text = eintraege[offen]?.text || "";
    const stelle = feld ? feld.selectionStart : text.length;
    aendern(offen, { text: text.slice(0, stelle) + marke + text.slice(stelle) });
    // Den Cursor hinter die eingesetzte Marke — sonst tippt man mitten hinein.
    requestAnimationFrame(() => {
      feld?.focus();
      feld?.setSelectionRange(stelle + marke.length, stelle + marke.length);
    });
  };

  const speichern = async () => {
    if (!daten) return;
    const ohneNamen = LISTEN.some((l) => (daten[l.id] || []).some((e) => !e.label.trim()));
    if (ohneNamen) { setMeldung("Jeder Baustein braucht einen Namen — sonst ist er nicht wiederzufinden."); return; }
    try {
      await sichern.mutateAsync(daten);
      setMeldung("Bibliothek gespeichert.");
    } catch (fehler) {
      setMeldung(errorText(fehler));
    }
  };

  return (
    <div className="vg-bib-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="vg-bib" role="dialog" aria-modal="true" aria-label="Textbausteine">
        <div className="vg-bib-kopf">
          <div>
            <p className="invoice-kicker">Bibliothek</p>
            <h2>Textbausteine</h2>
          </div>
          <button className="invoice-icon-button" type="button" onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div className="vg-bib-reiter" role="tablist">
          {LISTEN.map((eintrag) => (
            <button
              key={eintrag.id}
              type="button"
              role="tab"
              aria-selected={liste === eintrag.id}
              className={liste === eintrag.id ? "is-an" : ""}
              onClick={() => setListe(eintrag.id)}
            >
              {eintrag.titel}
              <b>{(daten?.[eintrag.id] || []).length}</b>
            </button>
          ))}
        </div>

        <p className="vg-bib-sinn">
          {LISTEN.find((l) => l.id === liste)?.sinn}{" "}
          Der mit ★ markierte Baustein wird in einen neuen Beleg vorbelegt.
        </p>

        <div className="vg-bib-liste">
          {bestand.isLoading && <p className="invoice-editor-hint">Bibliothek wird geladen …</p>}
          {bestand.isError && <p className="invoice-inline-error">{errorText(bestand.error)}</p>}
          {daten && eintraege.length === 0 && (
            <p className="invoice-editor-hint">In dieser Liste steht noch kein Baustein.</p>
          )}
          {eintraege.map((eintrag, index) => (
            <div className={"vg-bib-eintrag" + (offen === index ? " is-offen" : "")} key={index}>
              <div className="vg-bib-zeile">
                <button
                  type="button"
                  className={"vg-bib-stern" + (eintrag.label === standard ? " is-an" : "")}
                  onClick={() => zumStandard(index)}
                  aria-pressed={eintrag.label === standard}
                  title={eintrag.label === standard ? "Ist der Standard" : "Als Standard setzen"}
                >
                  ★
                </button>
                <button type="button" className="vg-bib-oeffnen" onClick={() => setOffen(offen === index ? null : index)}>
                  <strong>{eintrag.label || "Ohne Namen"}</strong>
                  <small>{(eintrag.text || "").split("\n").find((z) => z.trim()) || "noch kein Text"}</small>
                </button>
                <button type="button" className="vg-bib-loeschen" onClick={() => loeschen(index)} aria-label={`${eintrag.label} löschen`} title="Löschen">
                  🗑
                </button>
              </div>

              {offen === index && (
                <div className="vg-bib-form">
                  <label className="invoice-editor-field">
                    <span>Name</span>
                    <input value={eintrag.label} onChange={(event) => aendern(index, { label: event.target.value })} />
                  </label>
                  <div className="vg-bib-platzhalter">
                    {PLATZHALTER.map((p) => (
                      <button type="button" key={p.marke} title={p.hilfe} onClick={() => platzhalterEinfuegen(p.marke)}>
                        {p.marke.replace(/[{}]/g, "")}
                      </button>
                    ))}
                  </div>
                  <textarea
                    ref={textfeld}
                    rows={7}
                    value={eintrag.text}
                    onChange={(event) => aendern(index, { text: event.target.value })}
                    placeholder="Absätze mit einer Leerzeile trennen."
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {meldung && <p className="invoice-inline-note" role="status">{meldung}</p>}

        <div className="vg-bib-fuss">
          <button className="invoice-button" type="button" onClick={anlegen} disabled={!daten}>+ Neuer Baustein</button>
          <span className="vg-bib-stand">{geaendert ? "Ungespeicherte Änderungen" : "Alles gespeichert"}</span>
          <button className="invoice-button" type="button" onClick={onClose}>Schließen</button>
          <button
            className="invoice-button primary"
            type="button"
            onClick={speichern}
            disabled={!daten || !geaendert || sichern.isPending}
          >
            {sichern.isPending ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
